/**
 * The criminal ladder as DATA (v0.6 "The Other Business", ADR-015 + DESIGN).
 * Five tiers of run-level upgrades. Each upgrade's effects: passive `income`
 * ($/tick — the exponential engine), `relapseAdd` (the irony engine → M8),
 * `deaPerTick` (federal attention, deeper = hotter), and `deaMitigate` (buy
 * officials to cool it — there is no natural decay).
 *
 * v0.15 "The Fixer" DECOUPLED the tree (RFC §3.1, G1): NO upgrade both earns
 * AND mitigates. Earners carry `income` (+`deaPerTick`/`relapseAdd`);
 * mitigators carry `deaMitigate` ONLY. The five old hybrids each split into a
 * pure earner (keeps its id) + a pure mitigator sibling (see
 * `CRIMINAL_HYBRID_SPLIT` — the SCHEMA 9→10 migration grants old owners both
 * halves). Earner income was cut ÷4 to match the ×4 pacing pass; the live
 * master dial is `CRIMINAL.incomeScale` (Control-Room tunable).
 *
 * A LEAF module — imports only tunables (the AccountState/RunState imports are
 * type-only). Nothing here mutates state.
 */

import { CRIMINAL, upgradeCost } from "@/data/tunables";

/** Effects an owned criminal upgrade contributes (all optional, all per-level).
 *  DECOUPLED (v0.15 G1): a def carries EITHER income (an earner) OR deaMitigate
 *  (a mitigator), never both. */
export interface CriminalEffect {
  income?: number; // $/tick, ×level
  relapseAdd?: number; // relapse points, ×level
  deaPerTick?: number; // DEA/tick, ×level
  deaMitigate?: number; // DEA/tick removed, ×level (buying protection)
}

export interface CriminalUpgradeDef {
  id: string;
  tier: number; // 1..5
  title: string;
  blurb: string; // draft satire copy (v0.9 owns the final pass)
  baseCost: number; // funding for level 0→1
  maxLevel: number;
  effect: CriminalEffect;
}

export const CRIMINAL_TIER_TITLES = [
  "Confiscated Drug Sales",
  "Black Market Supply Chain",
  "Fentanyl Import",
  "Cartel Partnership",
  "Political Protection",
] as const;

export const CRIMINAL_UPGRADES = [
  // ── Tier 1 · Confiscated Drug Sales ──
  {
    id: "streetContact",
    tier: 1,
    title: "Street Contact",
    blurb: "A guy who knows a guy. The confiscated inventory doesn't inventory itself.",
    baseCost: 500,
    maxLevel: 5,
    effect: { income: 3, deaPerTick: 0.4, relapseAdd: 1 },
  },
  {
    id: "betterConfiscation",
    tier: 1,
    title: "Better Confiscation Protocols",
    blurb: "The more you confiscate, the more there is to sell. Compliance as supply chain.",
    baseCost: 900,
    maxLevel: 5,
    effect: { income: 5, deaPerTick: 0.5, relapseAdd: 1 },
  },
  {
    id: "rotatingFence",
    tier: 1,
    title: "Rotating Fence",
    blurb: "Never the same buyer twice. Move the product, keep the ledger short.",
    baseCost: 1400,
    maxLevel: 5,
    effect: { income: 8, deaPerTick: 0.3, relapseAdd: 1 },
  },
  {
    id: "quietBuyers",
    tier: 1,
    title: "Quiet Buyers",
    blurb: "Untraceable, allegedly. The ones who never call twice draw no attention.",
    baseCost: 1400,
    maxLevel: 5,
    effect: { deaMitigate: 0.3 },
  },
  // ── Tier 2 · Black Market Supply Chain ──
  {
    id: "streetDistributor",
    tier: 2,
    title: "Street Distributor",
    blurb: "Your patients relapse; your distributor is there for them. Vertical integration.",
    baseCost: 3000,
    maxLevel: 5,
    effect: { income: 18, deaPerTick: 0.9, relapseAdd: 2 },
  },
  {
    id: "territoryExpansion",
    tier: 2,
    title: "Territory Expansion",
    blurb: "More corners, more customers, more former customers.",
    baseCost: 5000,
    maxLevel: 5,
    effect: { income: 28, deaPerTick: 1.1, relapseAdd: 2 },
  },
  {
    id: "lawEnforcementContact",
    tier: 2,
    title: "Law Enforcement Contact",
    blurb: "A friend on the force who mishears things. Federal heat, professionally cooled.",
    baseCost: 7000,
    maxLevel: 5,
    effect: { deaMitigate: 1.2 },
  },
  {
    id: "moneyLaundering",
    tier: 2,
    title: "Money Laundering Operation",
    blurb: "The facility's books have never looked cleaner. Literally.",
    baseCost: 9000,
    maxLevel: 5,
    effect: { income: 40, deaPerTick: 0.8, relapseAdd: 2 },
  },
  {
    id: "cleanBooks",
    tier: 2,
    title: "A Second Set of Books",
    blurb: "One ledger for the auditors, one for reality. The auditors prefer the first.",
    baseCost: 9000,
    maxLevel: 5,
    effect: { deaMitigate: 0.5 },
  },
  // ── Tier 3 · Fentanyl Import ──
  {
    id: "shellCompany",
    tier: 3,
    title: "Shell Company",
    blurb: "Registered in a state that doesn't ask. Owns nothing, moves everything.",
    baseCost: 18000,
    maxLevel: 5,
    effect: { income: 80, deaPerTick: 1.6, relapseAdd: 3 },
  },
  {
    id: "portContact",
    tier: 3,
    title: "Port Contact",
    blurb: "Container 4471 was never scanned. It's fine. It's medical supplies*.",
    baseCost: 26000,
    maxLevel: 5,
    effect: { income: 120, deaPerTick: 2.0, relapseAdd: 3 },
  },
  {
    id: "diplomaticPouch",
    tier: 3,
    title: "Diplomatic Pouch",
    blurb: "It moves the product across every border. The treaty did not anticipate you.",
    baseCost: 34000,
    maxLevel: 5,
    effect: { income: 150, deaPerTick: 1.4, relapseAdd: 3 },
  },
  {
    id: "sealedManifest",
    tier: 3,
    title: "Sealed Manifest",
    blurb: "Immune to search by treaty. Nobody asks what a diplomat is carrying.",
    baseCost: 34000,
    maxLevel: 5,
    effect: { deaMitigate: 1.5 },
  },
  {
    id: "customsOfficial",
    tier: 3,
    title: "Customs Official (bribed)",
    blurb: "He waves it through and waves at you. A warm relationship.",
    baseCost: 42000,
    maxLevel: 5,
    effect: { deaMitigate: 2.5 },
  },
  // ── Tier 4 · Cartel Partnership ──
  {
    id: "territoryProtection",
    tier: 4,
    title: "Territory Protection",
    blurb: "The competition has retired. Suddenly and completely.",
    baseCost: 80000,
    maxLevel: 5,
    effect: { income: 275, deaPerTick: 2.4, relapseAdd: 4 },
  },
  {
    id: "unlimitedSupply",
    tier: 4,
    title: "Unlimited Supply Contract",
    blurb: "Demand is a solved problem — you manufacture it upstairs.",
    baseCost: 120000,
    maxLevel: 5,
    effect: { income: 450, deaPerTick: 2.8, relapseAdd: 5 },
  },
  {
    id: "politicalInsulation",
    tier: 4,
    title: "Political Insulation",
    blurb: "Investigations get reassigned. Investigators get promoted, elsewhere.",
    baseCost: 160000,
    maxLevel: 5,
    effect: { deaMitigate: 3.5 },
  },
  // ── Tier 5 · Political Protection ──
  {
    id: "friendlyLegislator",
    tier: 5,
    title: "Friendly Legislator",
    blurb: "He named a wing after you. You wrote the bill that funds it.",
    baseCost: 300000,
    maxLevel: 5,
    effect: { income: 800, deaPerTick: 3.0, relapseAdd: 5 },
  },
  {
    id: "committeeAssignment",
    tier: 5,
    title: "Committee Assignment",
    blurb: "You now sit on the panel that funds the market you run. Synergy.",
    baseCost: 450000,
    maxLevel: 5,
    effect: { income: 1250, deaPerTick: 2.6, relapseAdd: 5 },
  },
  {
    id: "oversightCapture",
    tier: 5,
    title: "Oversight Seat",
    blurb: "You now oversee the agency that oversees you. The minutes reflect no concerns.",
    baseCost: 450000,
    maxLevel: 5,
    effect: { deaMitigate: 2.0 },
  },
  {
    id: "regulatoryCapture",
    tier: 5,
    title: "Regulatory Capture",
    blurb: "The rules are yours now. You made them profitable — for you.",
    baseCost: 650000,
    maxLevel: 5,
    effect: { income: 1875, deaPerTick: 3.4, relapseAdd: 5 },
  },
  {
    id: "rewrittenRules",
    tier: 5,
    title: "Rewritten Rules",
    blurb: "You made them reasonable — for you. What was once a raid is now paperwork.",
    baseCost: 650000,
    maxLevel: 5,
    effect: { deaMitigate: 3.0 },
  },
  {
    id: "fullImmunity",
    tier: 5,
    title: "Full Immunity",
    blurb: "There is no raid. There was never a raid. What raid?",
    baseCost: 1000000,
    maxLevel: 3,
    effect: { deaMitigate: 8.0 },
  },
] as const satisfies readonly CriminalUpgradeDef[];

export type CriminalUpgradeId = (typeof CRIMINAL_UPGRADES)[number]["id"];

/**
 * v0.15 §8.5 hybrid-split map: old hybrid id (kept as the pure EARNER) → the
 * new pure-MITIGATOR sibling that carries the deaMitigate it lost. The SCHEMA
 * 9→10 migration reads an old owner's hybrid level and grants the sibling the
 * SAME level, so no one loses what they bought. Single source of truth for both
 * the data above and SaveManager's migration. */
export const CRIMINAL_HYBRID_SPLIT: Readonly<Record<string, CriminalUpgradeId>> = {
  rotatingFence: "quietBuyers",
  moneyLaundering: "cleanBooks",
  diplomaticPouch: "sealedManifest",
  committeeAssignment: "oversightCapture",
  regulatoryCapture: "rewrittenRules",
};

const BY_ID = new Map<string, CriminalUpgradeDef>(CRIMINAL_UPGRADES.map((u) => [u.id, u]));

/** Fail-loud lookup — an unknown id is a programming error, not a fallback. */
export function criminalUpgradeDef(id: string): CriminalUpgradeDef {
  const def = BY_ID.get(id);
  if (!def) {
    throw new Error(`criminalUpgradeDef: unknown criminal upgrade id "${id}"`);
  }
  return def;
}

/** Reputation-agnostic cost to buy the NEXT level (priced at the current level). */
export function criminalUpgradeCostAt(def: CriminalUpgradeDef, level: number): number {
  return upgradeCost(def.baseCost, CRIMINAL.costGrowth, level);
}
